/** @format */

import {
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  FileTextOutlined,
  IdcardOutlined,
  LockOutlined,
  PlusOutlined,
  SwapOutlined,
  TagOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import moment from "moment";
import React, { memo } from "react";
import {
  TGiornoPersonaSettimana,
  TPersonaSettimana,
  TTurnoSettimana,
} from "../../../../redux-modules/declarations/maps/turniSettimana";
import {
  AVVISO_ORE_SETTIMANALI,
  avvisiDellaSettimana,
  descrizioneContratto,
  finisceIlGiornoDopo,
  giornoOccupato,
  iniziali,
  messaggiAvvisiTurno,
  nomeCompleto,
  orarioTurno,
  sottotitoloTurno,
  tinta,
  TUnita,
} from "../format";
import { RigaPersonaStile } from "../styles";
import { BloccoTotali } from "./BloccoTotali";

export type TApriAssegnazione = (personaId: number, data: string, ancora: DOMRect) => void;
export type TApriTurno = (personaId: number, turnoId: number) => void;

interface Props {
  persona: TPersonaSettimana;
  oggi: string;
  unita: TUnita;
  puoCreare: boolean;
  puoCrearePassato: boolean;
  /** vista pulita: niente avvisi, "!" e colori delle anomalie, solo la pianificazione */
  pulita: boolean;
  onApriAssegnazione: TApriAssegnazione;
  onApriTurno: TApriTurno;
}

export const IconaNotte = (): JSX.Element => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
  </svg>
);

/** testo della nota per il title della cella: le note possono arrivare a 5000 caratteri */
const anteprimaNota = (nota: string): string =>
  nota.length > 240 ? `Nota: ${nota.slice(0, 240).trim()}…` : `Nota: ${nota}`;

const etichettaNonLavorativo = (nome: string): string =>
  nome ? nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase() : "Riposo";

const ChipTurno = ({
  turno: turnoCompleto,
  data,
  oggi,
  avvisi,
  pulita,
  nomePersona,
  onClick,
}: {
  turno: TTurnoSettimana;
  data: string;
  oggi: string;
  avvisi: string[];
  pulita: boolean;
  nomePersona: string;
  onClick: () => void;
}): JSX.Element => {
  // in vista pulita la mancata timbratura si vede come un turno ancora da svolgere
  const turno: TTurnoSettimana =
    pulita && turnoCompleto.stato === "mancata_timbratura"
      ? { ...turnoCompleto, stato: "da_svolgere" }
      : turnoCompleto;

  if (!turno.lavorativo) {
    return (
      <button
        type="button"
        className="turno non_lavorativo"
        onClick={onClick}
        aria-label={`${nomePersona}, ${moment(data).format("dddd D MMMM")}, ${turno.nome}${
          turno.note ? ", con nota" : ""
        }`}
      >
        <span className="orario">{etichettaNonLavorativo(turno.nome)}</span>
        {turno.note && (
          <span className="dettagli">
            <span title={anteprimaNota(turno.note)}>
              <FileTextOutlined />
              Nota
            </span>
          </span>
        )}
      </button>
    );
  }

  const sotto = sottotitoloTurno(turno, data, oggi);
  const notte = finisceIlGiornoDopo(turno);
  const conDettagli =
    notte ||
    !!turno.sede ||
    turno.fine_nascosta ||
    turno.cambio_richiesto ||
    !!turno.evento ||
    !!turno.note;
  const descrizione = [
    nomePersona,
    moment(data).format("dddd D MMMM"),
    `turno ${turno.ora_inizio} ${turno.ora_fine}`,
    sotto,
    turno.note ? "con nota" : "",
    ...avvisi,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <button
      type="button"
      className={`turno ${turno.stato}${avvisi.length ? " con-avviso" : ""}`}
      onClick={onClick}
      aria-label={descrizione}
    >
      <span className="orario">
        {turno.stato === "timbrato" && <LockOutlined />}
        {turno.stato === "mancata_timbratura" && <ExclamationCircleOutlined />}
        {turno.stato === "in_corso" && <span className="punto-vivo" aria-hidden="true" />}
        <span>{orarioTurno(turno)}</span>
        {notte && <span className="giorno-dopo">+1</span>}
      </span>
      {sotto && <span className="sotto">{sotto}</span>}
      {conDettagli && (
        <span className="dettagli">
          {notte && (
            <span title="Turno notturno">
              <IconaNotte />
              Notte
            </span>
          )}
          {turno.sede && (
            <span title="Sede diversa da quella abituale">
              <EnvironmentOutlined />
              {turno.sede}
            </span>
          )}
          {turno.fine_nascosta && (
            <span title="Fine turno nascosta al dipendente">
              <EyeInvisibleOutlined />
              Fine nascosta
            </span>
          )}
          {turno.cambio_richiesto && (
            <span title="Il dipendente ha chiesto un cambio turno">
              <SwapOutlined />
              Cambio chiesto
            </span>
          )}
          {turno.evento && (
            <span title="Evento">
              <TagOutlined />
              {turno.evento}
            </span>
          )}
          {turno.note && (
            <span title={anteprimaNota(turno.note)}>
              <FileTextOutlined />
              Nota
            </span>
          )}
        </span>
      )}
      {avvisi.length > 0 && (
        <Tooltip
          title={avvisi.map(a => (
            <div key={a}>{a}</div>
          ))}
        >
          <span className="segno-avviso" aria-hidden="true">
            !
          </span>
        </Tooltip>
      )}
    </button>
  );
};

const Cella = ({
  persona,
  giorno,
  oggi,
  puoCreare,
  puoCrearePassato,
  pulita,
  onApriAssegnazione,
  onApriTurno,
}: {
  persona: TPersonaSettimana;
  giorno: TGiornoPersonaSettimana;
  oggi: string;
  puoCreare: boolean;
  puoCrearePassato: boolean;
  pulita: boolean;
  onApriAssegnazione: TApriAssegnazione;
  onApriTurno: TApriTurno;
}): JSX.Element => {
  const nome = nomeCompleto(persona);
  const giornaliere = giorno.assenze.filter(a => a.unita === "GIORNI");
  const aOre = giorno.assenze.filter(a => a.unita === "ORE");
  const anomalieGiorno = pulita
    ? []
    : giorno.segnalazioni.filter(s => s.livello === "anomalia" && s.shift_planning_id === null);
  // nei giorni passati serve anche ShiftPlanning/Cancreatepastdays; sovrapposizioni, turni già timbrati
  // e mesi bloccati li controlla il backend
  const puoAggiungere =
    puoCreare && !giorno.bloccato && (giorno.data >= oggi || puoCrearePassato);
  const classi = [
    "cella",
    giorno.data === oggi ? "e-oggi" : "",
    !giorno.previsto ? "non-prevista" : "",
    giorno.festivita ? "festiva" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classi}>
      {giorno.festivita && <span className="etichetta-festa">{giorno.festivita}</span>}
      {anomalieGiorno.length > 0 && (
        <Tooltip
          title={anomalieGiorno.map((s, i) => (
            <div key={i}>{s.descrizione}</div>
          ))}
        >
          <span
            className="segnalazioni-giorno"
            aria-label={anomalieGiorno.map(s => s.descrizione).join(", ")}
          >
            !
          </span>
        </Tooltip>
      )}
      {giornaliere.map(a => (
        <div
          key={`a${a.richiesta_id}`}
          className={`assenza${a.stato === "in_attesa" ? " in-attesa" : ""}`}
          style={{ background: tinta(a.colore) }}
          title={a.stato === "in_attesa" ? `${a.nome}, richiesta in attesa` : a.nome}
        >
          {a.nome}
          {a.stato === "in_attesa" ? " (in attesa)" : ""}
        </div>
      ))}
      {giorno.turni.map(turno => (
        <ChipTurno
          key={turno.id}
          turno={turno}
          data={giorno.data}
          oggi={oggi}
          avvisi={pulita ? [] : messaggiAvvisiTurno(turno, persona.avvisi_contratto, "giorno")}
          pulita={pulita}
          nomePersona={nome}
          onClick={(): void => onApriTurno(persona.id, turno.id)}
        />
      ))}
      {aOre.map(a => (
        <div
          key={`o${a.richiesta_id}`}
          className={`assenza ore${a.stato === "in_attesa" ? " in-attesa" : ""}`}
          style={{ background: tinta(a.colore, 0.3) }}
          title={a.stato === "in_attesa" ? "Richiesta in attesa" : undefined}
        >
          {a.nome} {a.dalle}&ndash;{a.alle}
        </div>
      ))}
      {puoAggiungere && (
        <button
          type="button"
          className={`aggiungi${giornoOccupato(giorno) ? "" : " vuota"}`}
          aria-label={`Assegna un turno a ${nome}, ${moment(giorno.data).format("dddd D MMMM")}`}
          onClick={(e): void =>
            onApriAssegnazione(persona.id, giorno.data, e.currentTarget.getBoundingClientRect())
          }
        >
          <PlusOutlined />
        </button>
      )}
    </div>
  );
};

const RigaPersonaBase = ({
  persona,
  oggi,
  unita,
  puoCreare,
  puoCrearePassato,
  pulita,
  onApriAssegnazione,
  onApriTurno,
}: Props): JSX.Element => {
  const versioni = persona.contratto.versioni;
  const prima = versioni[0];
  const seconda = persona.contratto.cambia_in_settimana ? versioni[1] : undefined;
  const singleBadge = versioni.some(v => v.single_badge);
  const oltre = persona.avvisi_contratto.some(a => a.codice === AVVISO_ORE_SETTIMANALI);
  const avvisiSettimana = pulita ? [] : avvisiDellaSettimana(persona.avvisi_contratto);
  const nome = nomeCompleto(persona);

  return (
    <RigaPersonaStile className="riga">
      <div className="col-nome persona">
        <div className="alto">
          <span className="avatar" aria-hidden="true">
            {persona.avatar ? <img src={persona.avatar} alt="" /> : iniziali(persona)}
          </span>
          <span className="nome" title={nome}>
            {nome}
          </span>
          {singleBadge && (
            <Tooltip title="Single badge: una timbratura al giorno">
              <span className="icona-piccola">
                <IdcardOutlined />
              </span>
            </Tooltip>
          )}
        </div>
        {prima && (
          <div className="contratto">
            {descrizioneContratto(prima.ore_settimanali, prima.giorni_settimanali)}
          </div>
        )}
        {prima?.tipologia && <div className="contratto">{prima.tipologia.nome}</div>}
        {seconda && (
          <div className="contratto cambio">
            Da {moment(seconda.dal).format("ddd D")}:{" "}
            {descrizioneContratto(seconda.ore_settimanali, seconda.giorni_settimanali)}
          </div>
        )}
      </div>

      {persona.giorni.map(giorno => (
        <Cella
          key={giorno.data}
          persona={persona}
          giorno={giorno}
          oggi={oggi}
          puoCreare={puoCreare}
          puoCrearePassato={puoCrearePassato}
          pulita={pulita}
          onApriAssegnazione={onApriAssegnazione}
          onApriTurno={onApriTurno}
        />
      ))}

      <div className="col-totali">
        <BloccoTotali
          assegnati={persona.totali.minuti_assegnati}
          previsti={persona.totali.minuti_previsti}
          delta={persona.totali.delta_minuti}
          unita={unita}
          oltreContratto={oltre}
          nota={
            persona.totali.giorni_assenza > 0
              ? `Previste ridotte di ${persona.totali.giorni_assenza} ${
                  persona.totali.giorni_assenza === 1 ? "giorno" : "giorni"
                } di assenza`
              : undefined
          }
        />
        {avvisiSettimana.map((a, i) => (
          <div key={i} className="avviso-settimana" style={{ marginTop: 4 }}>
            <Tooltip title={a.messaggio}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "#8A5300" }}>
                <WarningOutlined /> {a.messaggio}
              </span>
            </Tooltip>
          </div>
        ))}
      </div>
    </RigaPersonaStile>
  );
};

/** Una riga si ridisegna solo se cambiano i suoi dati, l'unità o "oggi". */
export const RigaPersona = memo(RigaPersonaBase);
